/**
 * The form-to-API logic for a redemption code, kept out of the component so the
 * rules can be tested without rendering a modal.
 *
 * Who a code reaches: PUBLIC is any school (or any district) that has the code,
 * so it names none; PRIVATE names the one organization, and a private teacher
 * code lists emails instead.
 *
 * An INDIVIDUAL code (a teacher at the paywall) differs from a school or
 * district one in that it cannot change the length of the subscription, and its
 * discount is either a final price or a percentage off the plan — one of the
 * two, never both. The backend enforces every one of these; the checks here only
 * spare the admin a round trip.
 *
 * The form speaks dollars and weeks. The API stays in cents, so a price is
 * converted once, here, and an admin never has to work out how many cents $500 is.
 */

export const INDIVIDUAL = "INDIVIDUAL";
export const PRIVATE = "PRIVATE";

/** The state picker, as teachers see it at onboarding. Values are what the API filters on. */
export const US_STATES = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"], ["CA", "California"],
  ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"], ["DC", "District of Columbia"], ["FL", "Florida"],
  ["GA", "Georgia"], ["GU", "Guam"], ["HI", "Hawaii"], ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"],
  ["IA", "Iowa"], ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"],
  ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"], ["MO", "Missouri"],
  ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"],
  ["NM", "New Mexico"], ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"],
  ["OK", "Oklahoma"], ["OR", "Oregon"], ["PA", "Pennsylvania"], ["PR", "Puerto Rico"], ["RI", "Rhode Island"],
  ["SC", "South Carolina"], ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"],
  ["VT", "Vermont"], ["VI", "Virgin Islands"], ["VA", "Virginia"], ["WA", "Washington"], ["WV", "West Virginia"],
  ["WI", "Wisconsin"], ["WY", "Wyoming"],
];

/**
 * A code's dates are whole days for customers in the US, wherever the admin
 * setting them is. "Activates on" opens the code when that day starts on the
 * East Coast, the first US time zone to reach it; "Expires on" keeps it working
 * until that day ends in Hawaii, the last. Read as UTC midnight instead, a code
 * set to expire on the 30th stopped working on the evening of the 29th in the US.
 */
export const OPENS_IN = "America/New_York";
export const CLOSES_IN = "Pacific/Honolulu";

const DATE_PARTS = { year: "numeric", month: "2-digit", day: "2-digit" };

/** How far `timeZone`'s clock is ahead of UTC at `instant`, in ms. */
const zoneOffsetMs = (instant, timeZone) => {
  const format = new Intl.DateTimeFormat("en-US", {
    ...DATE_PARTS,
    timeZone,
    hourCycle: "h23",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const p = Object.fromEntries(format.formatToParts(instant).map((part) => [part.type, Number(part.value)]));
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(instant / 1000) * 1000;
};

/** The instant a wall-clock time on `date` (YYYY-MM-DD) happens in `timeZone`. */
const wallClockIso = (date, [hour, minute, second, ms], timeZone) => {
  const [y, m, d] = date.split("-").map(Number);
  const wall = Date.UTC(y, m - 1, d, hour, minute, second, ms);
  // Twice, so a day that crosses a daylight-saving change still lands right.
  const first = wall - zoneOffsetMs(wall, timeZone);
  return new Date(wall - zoneOffsetMs(first, timeZone)).toISOString();
};

export const activationIso = (date) => wallClockIso(date, [0, 0, 0, 0], OPENS_IN);
export const expiryIso = (date) => wallClockIso(date, [23, 59, 59, 999], CLOSES_IN);

/** A stored instant back to the day the admin picked, for the date field. */
export const toDateInput = (iso, timeZone) =>
  iso ? new Intl.DateTimeFormat("en-CA", { ...DATE_PARTS, timeZone }).format(new Date(iso)) : "";

const isWhole = (value) => /^\d+$/.test(String(value).trim());

/**
 * "500", "$1,250.50" -> whole cents, or null when it is not an amount of money.
 * Read as digits, never through a float: 19.99 * 100 is 1998.9999999999998.
 */
export const dollarsToCents = (text) => {
  const clean = String(text ?? "").trim().replace(/[$,\s]/g, "");
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(clean);
  if (!match) return null;
  return Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0"));
};

/** Cents back to what the admin typed: "500", or "12.50" when there are cents. */
export const centsToDollars = (cents) => {
  if (cents == null) return "";
  const whole = Math.floor(cents / 100);
  const rest = cents % 100;
  return rest ? `${whole}.${String(rest).padStart(2, "0")}` : String(whole);
};

/** "6 weeks", "1 week". */
export const weeksLabel = (weeks) => `${weeks} ${Number(weeks) === 1 ? "week" : "weeks"}`;

const plural = (n, word) => `${n} ${word}${Number(n) === 1 ? "" : "s"}`;

/**
 * The plans a code of this audience may be pinned to: a school code to a
 * school plan, a district code to a district plan. Any other pairing could
 * never be redeemed, and the server refuses it.
 */
export const plansForAudience = (plans, audience) => plans.filter((p) => p.audience === audience);

/**
 * The plan to show for a code being edited. Editing a plan's price retires the
 * version a code was made for; the code now sells the version on sale, which
 * is the one in the picker.
 */
export const pickerPlanId = (row, plans) => {
  if (plans.some((p) => p.id === row.planId)) return String(row.planId);
  const stored = row.plan;
  const live =
    stored &&
    plans.find((p) => p.tier === stored.tier && p.audience === stored.audience && p.billingInterval === stored.billingInterval);
  return String(live ? live.id : row.planId);
};

/**
 * What the buyer would pay under this draft, in cents, or null while it is
 * incomplete. A school or district that sets no price pays the plan's; a teacher
 * code is never above the plan's price.
 */
export const previewChargeCents = (draft, plan) => {
  if (!plan) return null;
  if (draft.audience !== INDIVIDUAL) {
    if (String(draft.price ?? "").trim() === "") return plan.priceCents;
    return dollarsToCents(draft.price);
  }
  if (draft.discountType === "PERCENT") {
    if (!isWhole(draft.percentOff)) return null;
    const percent = Number(draft.percentOff);
    if (percent < 1 || percent > 99) return null;
    return Math.round((plan.priceCents * (100 - percent)) / 100);
  }
  const cents = dollarsToCents(draft.price);
  return cents == null ? null : Math.min(cents, plan.priceCents);
};

/**
 * The plan this draft renews onto: the one picked, else Gold for a school, else
 * the draft's own plan. A teacher's code renews on the plan they bought.
 */
export const renewalPlanFor = (draft, plans, plan) => {
  const picked = plans.find((p) => String(p.id) === String(draft.renewalPlanId));
  if (picked) return picked;
  if (draft.audience === "SCHOOL") {
    return plans.find((p) => p.audience === "SCHOOL" && p.tier === "GOLD" && (!plan || p.billingInterval === plan.billingInterval)) ?? plan;
  }
  return plan;
};

/** The first term, in words: "6 weeks", "1 year". */
export const termLabel = (draft, plan) => {
  if (isWhole(draft.durationWeeks) && Number(draft.durationWeeks) > 0) return weeksLabel(draft.durationWeeks);
  if (draft.legacyMonths) return plural(draft.legacyMonths, "month");
  if (!plan) return "";
  return plan.billingInterval === "MONTHLY" ? "1 month" : "1 year";
};

/** An error message for the admin, or null when the draft can be sent. */
export const validateDraft = (draft, plan, money) => {
  if (draft.name.trim().length < 2) return "Give the code a name. It is what the customer sees.";

  const isPrivate = draft.visibility === PRIVATE;
  if (isPrivate && draft.audience === INDIVIDUAL && draft.authorizedEmails.length === 0) {
    return "Add at least one email, or make the code public.";
  }
  if (isPrivate && draft.audience !== INDIVIDUAL && !draft.org && !draft.locked) {
    return `Choose the ${draft.audience === "SCHOOL" ? "school" : "district"} this code is for.`;
  }
  if (!draft.planId) return "Choose a plan.";
  if (!isWhole(draft.maxRedemptions) || Number(draft.maxRedemptions) < 1) {
    return "Enter how many times the code can be used, 1 or more.";
  }
  if (draft.locked) return null;

  if (draft.audience === INDIVIDUAL) {
    if (draft.discountType === "PERCENT") {
      if (!isWhole(draft.percentOff) || Number(draft.percentOff) < 1 || Number(draft.percentOff) > 99) {
        return "Enter a whole percentage between 1 and 99.";
      }
      return null;
    }
    const cents = dollarsToCents(draft.price);
    if (!cents) return "Enter the price teachers will pay, in dollars.";
    if (plan && cents >= plan.priceCents) return `The price must be lower than the plan's price (${money(plan.priceCents)}).`;
    return null;
  }

  if (String(draft.price).trim() !== "" && !dollarsToCents(draft.price)) {
    return "Enter the price in dollars, for example 500 or 499.50.";
  }
  const weeks = String(draft.durationWeeks).trim();
  if (weeks !== "" && (!isWhole(weeks) || Number(weeks) < 1 || Number(weeks) > 520)) {
    return "Enter the length in whole weeks, for example 6.";
  }
  return null;
};

const emptyToUndefined = (value) => (value === "" ? undefined : Number(value));
const emptyToNull = (value) => (String(value).trim() === "" ? null : Number(value));

export const buildCreateBody = (draft) => {
  const isPrivate = draft.visibility === PRIVATE;
  const common = {
    name: draft.name.trim(),
    code: draft.code.trim() || undefined,
    audience: draft.audience,
    visibility: draft.visibility,
    authorizedEmails: isPrivate ? draft.authorizedEmails : [],
    planId: Number(draft.planId),
    maxRedemptions: Number(draft.maxRedemptions),
    activatesAt: draft.activatesAt ? activationIso(draft.activatesAt) : undefined,
    expiresAt: draft.expiresAt ? expiryIso(draft.expiresAt) : undefined,
  };

  if (draft.audience === INDIVIDUAL) {
    return {
      ...common,
      customAmountCents: draft.discountType === "PERCENT" ? undefined : dollarsToCents(draft.price),
      percentOff: draft.discountType === "PERCENT" ? Number(draft.percentOff) : undefined,
    };
  }

  return {
    ...common,
    // Only a private code names an organization: a public one is for anyone.
    schoolId: isPrivate && draft.audience === "SCHOOL" ? draft.org.id : undefined,
    districtId: isPrivate && draft.audience === "DISTRICT" ? draft.org.id : undefined,
    customDurationWeeks: emptyToUndefined(String(draft.durationWeeks).trim()),
    customAmountCents: String(draft.price).trim() === "" ? undefined : dollarsToCents(draft.price),
    renewalPlanId: emptyToUndefined(draft.renewalPlanId),
  };
};

export const buildUpdateBody = (draft) => {
  const isPrivate = draft.visibility === PRIVATE;
  const body = {
    name: draft.name.trim(),
    visibility: draft.locked ? undefined : draft.visibility,
    authorizedEmails: isPrivate ? draft.authorizedEmails : [],
    planId: draft.locked ? undefined : Number(draft.planId),
    maxRedemptions: Number(draft.maxRedemptions),
    isActive: draft.isActive,
    // An emptied date field means "no date": null clears it on the server,
    // where leaving it out would keep the old one.
    activatesAt: draft.activatesAt ? activationIso(draft.activatesAt) : null,
    expiresAt: draft.expiresAt ? expiryIso(draft.expiresAt) : null,
  };

  if (draft.audience === INDIVIDUAL) {
    // Switching between a price and a percentage means clearing the other one,
    // which only an explicit null does.
    if (!draft.locked) {
      body.customAmountCents = draft.discountType === "PERCENT" ? null : dollarsToCents(draft.price);
      body.percentOff = draft.discountType === "PERCENT" ? Number(draft.percentOff) : null;
    }
    return body;
  }

  if (draft.locked) return body;

  // An older code's term in months stays until someone sets one in weeks.
  const weeks = String(draft.durationWeeks).trim();
  return {
    ...body,
    schoolId: draft.audience === "SCHOOL" && isPrivate ? draft.org?.id : undefined,
    districtId: draft.audience === "DISTRICT" && isPrivate ? draft.org?.id : undefined,
    customDurationWeeks: weeks === "" && draft.legacyMonths ? undefined : emptyToNull(weeks),
    customAmountCents: String(draft.price).trim() === "" ? null : dollarsToCents(draft.price),
    renewalPlanId: emptyToNull(draft.renewalPlanId),
  };
};

/** How a code's discount reads in the table. */
export const describeDiscount = (row, money) => {
  if (row.audience === INDIVIDUAL) {
    if (row.percentOff != null) return `${row.percentOff}% off`;
    if (row.customAmountCents != null) return `${money(row.customAmountCents)} first term`;
    return "";
  }
  if (row.customAmountCents == null && row.customDurationMonths == null && row.customDurationWeeks == null) return "";
  const term = row.customDurationWeeks != null
    ? ` for ${weeksLabel(row.customDurationWeeks)}`
    : row.customDurationMonths != null
    ? ` for ${plural(row.customDurationMonths, "month")}`
    : "";
  return `${row.customAmountCents != null ? money(row.customAmountCents) : "Plan price"}${term}`;
};

/** Who a code is for, in the table. */
export const describeReach = (row) => {
  if (row.audience === INDIVIDUAL) return row.visibility === PRIVATE ? "Listed teachers" : "Any teacher";
  if (row.audience === "SCHOOL") return row.school?.name ?? "Any school";
  return row.district?.name ?? "Any district";
};

/** A saved code as the form and the preview read it. */
export const draftFromRow = (row, plans) => ({
  id: row.id,
  locked: row.redeemedCount > 0,
  name: row.name ?? "",
  code: row.code,
  audience: row.audience,
  state: "",
  org: row.school ?? row.district ?? null,
  visibility: row.visibility,
  authorizedEmails: row.authorizedEmails || [],
  planId: pickerPlanId(row, plans),
  renewalPlanId: row.renewalPlanId != null ? String(row.renewalPlanId) : "",
  discountType: row.percentOff != null ? "PERCENT" : "AMOUNT",
  durationWeeks: row.customDurationWeeks != null ? String(row.customDurationWeeks) : "",
  legacyMonths: row.customDurationMonths ?? null,
  price: centsToDollars(row.customAmountCents),
  percentOff: row.percentOff != null ? String(row.percentOff) : "",
  maxRedemptions: String(row.maxRedemptions),
  isActive: row.isActive,
  activatesAt: toDateInput(row.activatesAt, OPENS_IN),
  expiresAt: toDateInput(row.expiresAt, CLOSES_IN),
});

/** "Gold", when the code rolls onto a different plan than it sells. Teachers never do. */
export const rollsOntoName = (row, plans) => {
  if (row.audience === INDIVIDUAL) return "";
  const plan = plans.find((p) => String(p.id) === pickerPlanId(row, plans));
  const renewal = renewalPlanFor(draftFromRow(row, plans), plans, plan);
  return renewal && renewal.name !== row.plan?.name ? renewal.name : "";
};

/** "Sep 17, 2026", on the same clock the form's date fields use. */
export const dayLabel = (iso, timeZone) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone }) : "";

/** A redemption's term: weeks when it has them, months on one from before weeks. */
export const redemptionTerm = (use) =>
  use.durationWeeks ? weeksLabel(use.durationWeeks) : plural(use.durationMonths, "month");
